/**
 * Where the 500 stops things.
 *
 * The ceiling is NOT a limit on what gets searched. Every accessible document
 * is searched and ranked; the ceiling decides how far down the ranked list a
 * person can page. Ten thousand matches are all considered, the best five
 * hundred are reachable, and twenty-five are on screen.
 *
 * Getting this backwards is the expensive mistake — a search that only looked
 * at 500 documents would silently miss the right policy, and nothing in the
 * interface would say so.
 */

/** Rows on one page. */
export const PAGE_SIZE = 25;

export interface PageMath {
  /** Matches reachable by paging, capped at the ceiling. */
  reachableHits: number;
  /** Zero-based index of the last openable page. */
  lastPage: number;
  /** True when the ceiling actually cut something off. */
  tooMany: boolean;
  /** 1-based first row on this page, for "Showing 1–25 of 43". */
  from: number;
  /** 1-based last row on this page. */
  to: number;
}

export function pageMath(
  totalHits: number,
  page: number,
  maxResults: number,
  pageSize = PAGE_SIZE,
): PageMath {
  const reachableHits = Math.min(totalHits, maxResults);
  const lastPage = Math.max(0, Math.ceil(reachableHits / pageSize) - 1);
  const safePage = Math.min(Math.max(0, page), lastPage);

  const from = reachableHits === 0 ? 0 : safePage * pageSize + 1;
  const to = Math.min(reachableHits, (safePage + 1) * pageSize);

  return {
    reachableHits,
    lastPage,
    tooMany: totalHits > maxResults,
    from,
    to,
  };
}

/**
 * The page numbers to draw, with gaps.
 *
 * Twenty pages of buttons is its own navigation problem, so this shows the
 * ends, the neighbourhood of the current page, and `null` where a gap belongs.
 */
export function pageWindow(current: number, lastPage: number, span = 2): (number | null)[] {
  if (lastPage < 0) return [];

  const wanted = new Set<number>([0, lastPage]);
  for (let p = current - span; p <= current + span; p++) {
    if (p >= 0 && p <= lastPage) wanted.add(p);
  }

  const sorted = [...wanted].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  let previous: number | null = null;

  for (const p of sorted) {
    if (previous !== null && p - previous > 1) out.push(null);
    out.push(p);
    previous = p;
  }
  return out;
}
